import * as z from 'zod/mini';
import {
  RACK_MAX_UNITS,
  RESERVATION_LABEL_MAX_LENGTH,
  RESERVATION_OWNER_MAX_LENGTH,
} from '../constants.ts';
import { idSchema, sortOrderSchema, timestampSchema } from './common.ts';

// A rack reservation (ROADMAP A11, migration 0046): a contiguous range of
// units held for gear that is planned but not yet installed. The range is
// 1-based from the bottom, the way a rack is labelled; a label says what it
// is for and an owner says who it is for. The range being ordered
// (start <= end) is enforced by the SQL CHECK and by `reservationRangeOk`
// here, since zod/mini leaves cross-field checks to the caller the way the
// floor schema does for a shape's whole/absent node link.

const unitSchema = z.int().check(z.gte(1), z.lte(RACK_MAX_UNITS));
const labelSchema = z
  .string()
  .check(z.trim(), z.minLength(1, 'Required'), z.maxLength(RESERVATION_LABEL_MAX_LENGTH));
const ownerSchema = z.string().check(z.trim(), z.maxLength(RESERVATION_OWNER_MAX_LENGTH));

export const rackReservationSchema = z.object({
  id: idSchema,
  rackId: idSchema,
  /** Lowest reserved unit, 1-based from the bottom of the rack. */
  startUnit: unitSchema,
  /** Highest reserved unit; the same as startUnit for a single-U reservation. */
  endUnit: unitSchema,
  /** What the units are reserved for. */
  label: z.string(),
  /** Who the units are held for; empty when not recorded. */
  owner: z.string(),
  sortOrder: sortOrderSchema,
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});
export type RackReservation = z.infer<typeof rackReservationSchema>;

const reservationFields = {
  startUnit: unitSchema,
  endUnit: unitSchema,
  label: labelSchema,
  owner: ownerSchema,
};

/** POST /api/racks/:id/reservations: the range, its label, and who it is for. */
export const rackReservationInputSchema = z.object({
  ...reservationFields,
  owner: z._default(ownerSchema, ''),
});
export type RackReservationInput = z.infer<typeof rackReservationInputSchema>;

/** PATCH /api/reservations/:id: any of a reservation's own facts, each optional. */
export const rackReservationPatchSchema = z.partial(z.object(reservationFields));
export type RackReservationPatch = z.infer<typeof rackReservationPatchSchema>;

export const rackReservationEnvelopeSchema = z.object({ reservation: rackReservationSchema });

/**
 * Whether a range is ordered (start at or below end). Both ends are already
 * bounded to real units by the schema; this is the one relation between them,
 * checked by the route before the row is written and by the SQL CHECK behind it.
 */
export function reservationRangeOk(range: { startUnit: number; endUnit: number }): boolean {
  return range.startUnit <= range.endUnit;
}

/** Whether two unit ranges overlap; used to guard a device off a reserved span. */
export function rangesOverlap(
  a: { startUnit: number; endUnit: number },
  b: { startUnit: number; endUnit: number },
): boolean {
  return a.startUnit <= b.endUnit && b.startUnit <= a.endUnit;
}

/** "U5", or "U5–U7" across a wider range, for a reservation's label on the drawing. */
export function reservationRangeLabel(range: { startUnit: number; endUnit: number }): string {
  return range.startUnit === range.endUnit
    ? `U${String(range.startUnit)}`
    : `U${String(range.endUnit)}–U${String(range.startUnit)}`;
}

/**
 * The U range a new device would occupy when it is added to a rack: it lands
 * below everything already mounted (the ordered list appends), so it takes
 * `height` units starting at unit `rackUnits - usedUnits` from the bottom.
 * Null when the rack's height is not set, since without it a unit number is
 * only a row from the top, not a place a reservation can name.
 */
export function landingSpan(
  usedUnits: number,
  rackUnits: number | null,
  height: number,
): { startUnit: number; endUnit: number } | null {
  if (rackUnits === null) {
    return null;
  }
  const endUnit = rackUnits - usedUnits;
  return { startUnit: endUnit - height + 1, endUnit };
}

/** The first reservation a range runs into, or null when it is clear. */
export function reservationClash<R extends { startUnit: number; endUnit: number }>(
  reservations: readonly R[],
  span: { startUnit: number; endUnit: number },
): R | null {
  return reservations.find((reservation) => rangesOverlap(reservation, span)) ?? null;
}
